import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Observable, Subject, fromEvent, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, share } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../components/itemsPerPage.component';
import { AutocompleteComponent, idsMatchingText } from '../../components/autocomplete/autocomplete.component';
import { Farm } from '../../models/farm.model';
import { FarmService } from '../../services/farm.service';
import { Campaign } from '../../models/campaign.model';
import { ListItem, Specy } from '../../models/lists.model';
import { Organisation } from '../../models/organisation.model';
import { Page } from '../../models/page.model';
import { Parcel, ParcelStatus } from '../../models/parcel.model';
import { CampaignYearPipe } from '../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../pipes/hectareConverter.pipe';
import { ToLabelPipe } from '../../pipes/toLabel.pipe';
import { CampaignService } from '../../services/campaign.service';
import { KeycloakService } from '../../services/keycloak.service';
import { ListType, ListsService } from '../../services/lists.service';
import { FarmerAuthorizationsService } from '../../services/farmer-authorizations.service';
import { ModalService } from '../../services/modal.service';
import { ParcelFilter, ParcelService } from '../../services/parcel.service';
import { UserService } from '../../services/user.service';
import { Tooltip } from '../../utils/Tooltip';
import { TooltipContent } from '../../utils/TooltipContent';
import { TOOLTIP_TEXTS } from '../../utils/tooltip-texts';
import { toBoolean, toNumber } from '../../utils/utils';
import {
  ConfirmDeleteParcelDialogComponent,
  ConfirmDeleteParcelDialogContext,
} from './confirm-delete-dialog.component';
import { ParcelDetailsDialogComponent, ParcelDetailsDialogContext } from './parcel-details-dialog.component';
import { OrganisationService } from '../../services/organisation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'parcels-cmp',
  templateUrl: 'parcels.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    FormsModule,
    Tooltip,
    TooltipContent,
    ItemsPerPageComponent,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    TranslatePipe,
    PaginationModule,
    TooltipModule,
    AutocompleteComponent,
  ],
  providers: [ParcelService, OrganisationService, UserService, FarmService],
})
export class ParcelComponent implements OnInit, AfterViewInit {
  error: any;

  @ViewChild('termPrefixFilterParcel') termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termNoInvoiceFilter') termNoInvoiceFilter: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;
  @ViewChild('termOwnerFilter') termOwnerFilter: ElementRef;

  prefixFilterParcel: string = '';
  // Farm, crop and organisation filters are autocompletes: the typed text filters
  // on its own, a picked suggestion filters on that exact item (id)
  prefixFilterExploitation: string = '';
  farmFilterId: number = null;
  prefixFilterIdParcel: number = null;
  specyFilter: number = null;
  specyFilterText: string = '';
  organisationFilterText: string = '';

  campaignFilter: number | string = null;
  organisationFilter: number | string = null;
  noInvoiceFilter: boolean = null;
  public advisorFilter: string | number = null;
  public ownerFilter: string = null;

  parcels: Parcel[] = [];
  page: number = 1;
  maxSize: number = 5;
  itemsPerPage: number = 25;
  numPages: number = 1;
  length: number = 0;

  showRestricted: boolean;
  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;
  isAdmin: boolean;

  speciesDict: Map<number, Specy>;
  // TODO utiliser plutôt juste les espèces utilisées par des parcelles
  // de l'utilisateur
  species: Specy[];
  campaigns: Campaign[];

  showOrganisationFilter = false;
  organisations: Organisation[] = [];
  isNegoce: boolean;
  isManager: boolean;
  isFarmerOnly: boolean;
  // Loaded async from block 6 (C2027-196); defaults to true so buttons do not flicker.
  canCreateParcel = true;

  tooltipTexts = TOOLTIP_TEXTS;
  // static pour garder entre affichages de la liste
  static filterToApply: ParcelFilter;
  // What the autocompletes show, kept with filterToApply between visits
  static filterTextsToApply: {
    farm: string;
    farmId: number;
    specy: string;
    specyId: number;
    organisation: string;
    organisationId: number;
  };
  static allFiltersToShow: boolean;

  // Incomplete-only view support
  forceIncompleteView: boolean = false;
  includeNoOrderAsIncomplete: boolean = false;
  private azoteToolKey: string = 'azote';
  private textFilterChanges = new Subject<void>();

  readonly farmLabel = (farm: Farm) => farm.name;
  readonly farmDetail = (farm: Farm) => [farm.postalCode, farm.city].filter(Boolean).join(' ');
  readonly specyLabel = (specy: Specy) => this.capitalizePipe.transform(specy.label || '', null);
  readonly organisationLabel = (organisation: Organisation) => organisation.name;
  readonly searchFarms = (term: string): Observable<Farm[]> =>
    this.farmService
      .getFarmsAdvanced(1, 20, 'name', 'asc', { filter: term }, null, null, null)
      .pipe(map((page) => page.content));
  private capitalizePipe = new CapitalizePipe();

  constructor(
    private parcelService: ParcelService,
    private listsService: ListsService,
    private campaignService: CampaignService,
    private organisationService: OrganisationService,
    private router: Router,
    private route: ActivatedRoute,
    private modal: ModalService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private farmService: FarmService,
    private farmerAuthorizationsService: FarmerAuthorizationsService,
  ) {
    this.showRestricted =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    this.isManager = KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.isFarmerOnly = KeycloakService.isFarmerOnly();
    this.showOrganisationFilter = this.isAdmin || this.isNegoce;
    this.farmerAuthorizationsService.can('createParcel').subscribe((allowed) => (this.canCreateParcel = allowed));
  }

  // ===== Data loading / paging ===================================================

  getParcelsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: ParcelFilter) {
    this.parcelService
      .getParcelsAdvanced(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<Parcel>) {
    this.parcels = page.content;
    // TODO tools
    this.getParcelStatuses();
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  addParcel() {
    this.router.navigate(['/parcels', 'new']);
  }

  deleteParcel(parcel: Parcel, event: any) {
    const context = new ConfirmDeleteParcelDialogContext();
    context.showClose = true;
    context.isBlocking = true;
    context.size = 'lg';
    context.parcelId = parcel.id;
    context.parent = this;
    this.modal.open(ConfirmDeleteParcelDialogComponent, context);
  }

  viewParcel(parcel: Parcel, event: any) {
    const context = new ParcelDetailsDialogContext();
    context.showClose = true;
    context.isBlocking = false;
    context.size = 'lg';
    context.parcelId = parcel.id;
    context.showEdit = true;
    this.modal.open(ParcelDetailsDialogComponent, context);
  }

  importParcel(parcel: Parcel) {
    const campaignId = this.campaigns && this.campaigns.length ? this.campaigns[0].id : null;
    if (campaignId) {
      this.router.navigate(['/parcels', 'new', campaignId, parcel.id]);
    }
  }

  getParcelStatuses() {
    if (!this.parcels || this.parcels.length === 0) return;

    const ids = this.parcels.map((x) => x.id);
    const index = new Map<number, Parcel>();
    this.parcels.forEach((p) => index.set(p.id, p));

    this.parcelService.getParcelStatuses(ids).subscribe((statuses) => {
      for (const s of statuses) {
        const p = index.get(s.parcelId);
        if (!p) {
          continue;
        }
        // uniquement utilisé dans cet écran
        // TODO l'ajouter dans le modèle ?
        (p as any)['canBeDeleted'] = this.canBeDeleted(s);
        (p as any)['canEditCrop'] = this.canEditCrop(s);
        (p as any).__status = s;
      }

      if (this.forceIncompleteView) {
        this.parcels = this.parcels.filter((p) => this.isIncompleteParcel((p as any).__status));
        this.length = this.parcels.length;
        this.numPages = Math.ceil(this.length / this.itemsPerPage) || 1;
      }
    });
  }

  canBeDeleted(status: ParcelStatus): boolean {
    if (!status) return false;
    if (status.countSuccessorParcels > 0) return false;
    if (status.toolStatuses) {
      for (const toolKey in status.toolStatuses) {
        const toolStatus = (status.toolStatuses as any)[toolKey];
        if (toolStatus && toolStatus.hasOrder) return false;
      }
    }
    return true;
  }

  private isIncompleteParcel(status: ParcelStatus): boolean {
    if (!status) return true;
    const toolStatuses: any = status.toolStatuses || {};
    const az = toolStatuses[this.azoteToolKey];
    if (!az) return this.includeNoOrderAsIncomplete;
    if (az.isComplete === true || az.completed === true) return false;
    return true;
  }

  private applyIncompleteFilter(shouldFilter: boolean) {
    this.forceIncompleteView = !!shouldFilter;
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      this.page = 1;
    }
  }

  canEditCrop(status: ParcelStatus): boolean {
    // Admin can edit everything including crop
    if (this.isAdmin) {
      return true;
    }

    // For other profiles, cannot edit crop if it has associated orders
    if (status && status.toolStatuses) {
      for (const toolKey in status.toolStatuses) {
        const toolStatuses: any = status.toolStatuses;
        const toolStatus = toolStatuses[toolKey];
        if (toolStatus.hasOrder) {
          return false;
        }
      }
    }
    return true;
  }

  ngOnInit() {
    let routeStatus: string = null;
    let routeOnlyIncomplete = false;
    let routeSpecyKey: string = null;
    let routeCampaignId: number = null;

    this.route.params.subscribe((params) => {
      routeStatus = params['status'];
      routeOnlyIncomplete = params['onlyIncomplete'] === '1' || params['onlyIncomplete'] === true;
      routeSpecyKey = params['specy'];
      if (params['campaignId']) routeCampaignId = +params['campaignId'];
    });

    // l'affichage des parcelles a besoin des espèces
    this.getSpecies().subscribe(() => {
      const hasRouteParams = routeStatus || routeOnlyIncomplete || routeSpecyKey || routeCampaignId;
      if (!ParcelComponent.filterToApply || hasRouteParams) {
        if (routeSpecyKey) {
          this.specyFilter = this.resolveSpecyIdFromKey(routeSpecyKey);
          const specy = this.specyFilter ? this.speciesDict.get(this.specyFilter) : null;
          this.specyFilterText = specy ? this.specyLabel(specy) : '';
        }
        if (routeCampaignId) this.campaignFilter = routeCampaignId;

        const completionFromRoute: boolean = routeOnlyIncomplete || routeStatus === 'incomplete' ? false : null;

        const orderFromRoute: boolean = routeOnlyIncomplete
          ? true
          : routeStatus === 'ordered'
            ? true
            : routeStatus === 'notOrdered'
              ? false
              : null;

        this.forceIncompleteView = routeOnlyIncomplete || routeStatus === 'incomplete';

        const campaignId = this.getCampaignFilterId();
        const entities = this.entityFilters();

        ParcelComponent.filterToApply = {
          prefixParcel: this.prefixFilterParcel || null,
          ...entities,
          prefixIdParcel: toNumber(this.prefixFilterIdParcel),

          campaignId,

          completionStatus: completionFromRoute,
          orderStatus: orderFromRoute,

          advisorId: this.advisorFilter && (this.advisorFilter + '').length >= 1 ? this.advisorFilter : null,
          ownerLogin: this.ownerFilter?.trim() || null,

          passage: null,
          postalCodeExp: null,
          postalCodePar: null,
          dateAvant: null,
          dateApres: null,
          onlyIds: null,
          suspended: null,
          defaultWeekIdSet: null,
          datePlanAfter: null,
          datePlanBefore: null,
          noInvoice: toBoolean(this.noInvoiceFilter),
        };
        this.saveFilterTexts();
      }
      this.updateUIWithFilters();
      this.onChangeTable();
    });
  }

  updateUIWithFilters() {
    const f = ParcelComponent.filterToApply;
    if (f) {
      if (this.isAdmin) {
        this.prefixFilterIdParcel = f.prefixIdParcel;
        this.noInvoiceFilter = toBoolean(f.noInvoice);
        if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
          this.termPrefixFilterIdParcel.nativeElement.value = this.prefixFilterIdParcel;
        }
      }
      if (this.isAdmin || this.isNegoce || this.isManager) {
        this.advisorFilter = f.advisorId || null;
        if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
          this.termAdvisorFilter.nativeElement.value = this.advisorFilter || '';
        }
      }
      if (!this.isFarmerOnly) {
        this.ownerFilter = f.ownerLogin || null;
        if (this.termOwnerFilter && this.termOwnerFilter.nativeElement) {
          this.termOwnerFilter.nativeElement.value = this.ownerFilter || '';
        }
      }

      this.prefixFilterParcel = f.prefixParcel;
      if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
        this.termPrefixFilterParcel.nativeElement.value = this.prefixFilterParcel;
      }
      const texts = ParcelComponent.filterTextsToApply;
      if (texts) {
        this.prefixFilterExploitation = texts.farm;
        this.farmFilterId = texts.farmId;
        this.specyFilterText = texts.specy;
        this.specyFilter = texts.specyId;
        this.organisationFilterText = texts.organisation;
        this.organisationFilter = texts.organisationId;
      }
      this.campaignFilter = toNumber(f.campaignId);
    }

    const hasActiveHiddenFilters =
      this.organisationFilter !== null || !!this.organisationFilterText || this.noInvoiceFilter !== null;

    if (ParcelComponent.allFiltersToShow || hasActiveHiddenFilters) {
      if (!this.showAllFilters) {
        this.toggleAllFilters();
      }
    }
  }

  ngAfterViewInit() {
    // TODO améliorer => pas de debounce quand clearFilter a été appelé
    const observables: Observable<{}>[] = [];

    if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
      const obs1 = fromEvent(this.termPrefixFilterParcel.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs1);
      obs1.subscribe(() => {
        this.prefixFilterParcel = <string>this.termPrefixFilterParcel.nativeElement.value;
      });
    }

    // Free text typed in the farm, crop and organisation autocompletes
    observables.push(
      this.textFilterChanges.pipe(
        debounceTime(300),
        map(() => ({})),
      ),
    );

    if (this.termCampaignFilter && this.termCampaignFilter.nativeElement) {
      const obs4 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
      observables.push(obs4);
    }

    if (this.isAdmin) {
      if (this.termNoInvoiceFilter && this.termNoInvoiceFilter.nativeElement) {
        const obs6 = fromEvent(this.termNoInvoiceFilter.nativeElement, 'change');
        observables.push(obs6);
      }
      if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
        const obs7 = fromEvent(this.termPrefixFilterIdParcel.nativeElement, 'keyup').pipe(
          debounceTime(300),
          distinctUntilChanged(),
        );
        observables.push(obs7);
        obs7.subscribe(() => {
          this.prefixFilterIdParcel = <number>this.termPrefixFilterIdParcel.nativeElement.value;
        });
      }
    }

    if (this.isAdmin || this.isNegoce) {
      if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
        const obs8 = fromEvent(this.termAdvisorFilter.nativeElement, 'keyup').pipe(
          debounceTime(300),
          distinctUntilChanged(),
        );
        observables.push(obs8);
        obs8.subscribe(() => {
          this.advisorFilter = <string>this.termAdvisorFilter.nativeElement.value;
        });
      }
    }

    if (this.termOwnerFilter && this.termOwnerFilter.nativeElement) {
      const obsOwner = fromEvent(this.termOwnerFilter.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obsOwner);
      obsOwner.subscribe(() => {
        this.ownerFilter = <string>this.termOwnerFilter.nativeElement.value;
      });
    }

    if (observables.length > 0) {
      merge(...observables)
        .pipe(debounceTime(10))
        .subscribe(() => {
          this.updateFilters();
        });
    }

    const self = this;
    this.getCampaigns().subscribe(function () {
      self.cdr.detectChanges();
    });
    this.getOrganisations().subscribe(function () {
      self.updateUIWithFilters();
      self.cdr.detectChanges();
    });
  }

  // ===== Reference data (species, campaigns, orgs) ==============================

  getSpecies(): Observable<Map<ListType, ListItem[]>> {
    // TODO handle error
    // share => plusieurs subscribers sans plusieurs requetes
    const obs = this.listsService.getListTypes(ListType, ListType.SPECIES).pipe(share());
    obs.subscribe((lists: Map<ListType, ListItem[]>) => {
      const species = lists.get(ListType.SPECIES) || [];
      this.species = species.sort((a, b) => a.label.localeCompare(b.label));
      this.speciesDict = new Map<number, Specy>();
      for (const specy of species) {
        this.speciesDict.set((specy as any).id, specy as any);
      }
    });
    return obs;
  }

  getCampaigns(): Observable<Campaign[]> {
    const obs = this.campaignService.getCampaigns().pipe(share());
    obs.subscribe((campaigns) => {
      this.campaigns = (campaigns || []).sort(Campaign.compare).reverse();
    });
    return obs;
  }

  getOrganisations(): Observable<Organisation[]> {
    if (this.isAdmin || this.isNegoce) {
      const obs = this.organisationService.getOrganisations().pipe(share());
      const self = this;
      obs.subscribe(function (orgs) {
        self.organisations = (orgs || []).sort(function (a, b) {
          return a.name.localeCompare(b.name);
        });
      });
      return obs;
    } else {
      return of([]);
    }
  }

  // ===== UI actions / filters ====================================================

  clearFilters() {
    this.prefixFilterParcel = null;
    this.prefixFilterExploitation = null;
    this.farmFilterId = null;
    this.prefixFilterIdParcel = null;
    this.specyFilter = null;
    this.specyFilterText = '';
    this.organisationFilterText = '';
    this.campaignFilter = null;
    this.organisationFilter = null;
    this.noInvoiceFilter = null;
    this.advisorFilter = null;
    this.ownerFilter = null;

    if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
      this.termPrefixFilterParcel.nativeElement.value = null;
    }
    if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
      this.termAdvisorFilter.nativeElement.value = null;
    }
    if (this.termOwnerFilter && this.termOwnerFilter.nativeElement) {
      this.termOwnerFilter.nativeElement.value = null;
    }
    if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
      this.termPrefixFilterIdParcel.nativeElement.value = null;
    }
    this.updateFilters();
  }

  updateFilters() {
    const campaignId = this.getCampaignFilterId();

    ParcelComponent.filterToApply = {
      prefixParcel: this.prefixFilterParcel || null,
      ...this.entityFilters(),
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),

      campaignId,

      completionStatus:
        ParcelComponent.filterToApply && typeof ParcelComponent.filterToApply.completionStatus === 'boolean'
          ? ParcelComponent.filterToApply.completionStatus
          : null,
      orderStatus: ParcelComponent.filterToApply ? ParcelComponent.filterToApply.orderStatus : null,

      advisorId: this.advisorFilter && (this.advisorFilter + '').length >= 1 ? this.advisorFilter : null,
      ownerLogin: this.ownerFilter?.trim() || null,

      passage: ParcelComponent.filterToApply ? ParcelComponent.filterToApply.passage : null,
      postalCodeExp: null,
      postalCodePar: null,
      dateAvant: null,
      dateApres: null,
      onlyIds: null,
      suspended: null,
      defaultWeekIdSet: null,
      datePlanAfter: null,
      datePlanBefore: null,
      noInvoice: toBoolean(this.noInvoiceFilter),
    };
    this.saveFilterTexts();

    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le binding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    this.getParcelsPaginated(pageNumber, null, null, ParcelComponent.filterToApply);
  }

  onChangeItemsPerPage(newValue: number) {
    // si itemsPerPage change et que page change ; par exemple page trop
    // grande pour le nombre d'éléments totaux => refresh de la pagination
    // mais si page reste identique, il faut le faire explicitement
    const page = this.page;
    this.cdr.detectChanges();
    if (page === this.page) {
      this.onChangeTable();
    }
  }

  getKmls() {
    const campaignId = this.getCampaignFilterId();

    const filter: ParcelFilter = {
      prefixParcel: this.prefixFilterParcel,
      ...this.entityFilters(),
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      campaignId,
      noInvoice: toBoolean(this.noInvoiceFilter),
      advisorId: this.advisorFilter,
      ownerLogin: this.ownerFilter?.trim() || null,
    };
    this.parcelService.getKmls(null, null, filter);
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      if (this.allFilters && this.allFilters.nativeElement) {
        this.allFilters.nativeElement.style.display = 'none';
      }
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      if (this.allFilters && this.allFilters.nativeElement) {
        this.allFilters.nativeElement.style.display = 'block';
      }
    }
    ParcelComponent.allFiltersToShow = this.showAllFilters;
  }

  // ===== Helpers =================================================================

  onFarmTextChange(text: string) {
    this.prefixFilterExploitation = text;
    this.farmFilterId = null;
    this.textFilterChanges.next();
  }

  onFarmSelected(farm: Farm) {
    this.prefixFilterExploitation = farm.name;
    this.farmFilterId = farm.id;
    this.updateFilters();
  }

  onSpecyTextChange(text: string) {
    this.specyFilterText = text;
    this.specyFilter = null;
    this.textFilterChanges.next();
  }

  onSpecySelected(specy: Specy) {
    this.specyFilterText = this.specyLabel(specy);
    this.specyFilter = (specy as any).id;
    this.updateFilters();
  }

  onOrganisationTextChange(text: string) {
    this.organisationFilterText = text;
    this.organisationFilter = null;
    this.textFilterChanges.next();
  }

  onOrganisationSelected(organisation: Organisation) {
    this.organisationFilterText = organisation.name;
    this.organisationFilter = organisation.id;
    this.updateFilters();
  }

  /** Farm, crop and organisation request params, from the autocompletes' state. */
  entityFilters(): Pick<ParcelFilter, 'prefixExploitation' | 'farmId' | 'specyId' | 'organisationId'> {
    // Crops and organisations are filtered by id only: typed text becomes the ids it matches
    const specyId =
      this.specyFilter ?? idsMatchingText(this.species, this.specyFilterText, this.specyLabel, (s: any) => s.id);
    const organisationId =
      this.getOrganisationFilterId() ??
      idsMatchingText(this.organisations, this.organisationFilterText, this.organisationLabel, (o) => o.id);
    return {
      prefixExploitation: this.farmFilterId ? null : this.prefixFilterExploitation?.trim() || null,
      farmId: this.farmFilterId,
      specyId,
      organisationId,
    };
  }

  private saveFilterTexts() {
    ParcelComponent.filterTextsToApply = {
      farm: this.prefixFilterExploitation,
      farmId: this.farmFilterId,
      specy: this.specyFilterText,
      specyId: this.specyFilter,
      organisation: this.organisationFilterText,
      organisationId: this.getOrganisationFilterId(),
    };
  }

  private getOrganisationFilterId(): number {
    const raw = this.organisationFilter;
    if (raw === null || raw === undefined) {
      return null;
    }

    if (typeof raw === 'number') {
      const parsedNumber = toNumber(raw);
      return parsedNumber === null ? null : parsedNumber;
    }

    const rawString = String(raw);
    const colonIndex = rawString.indexOf(':');
    const withoutPrefix = colonIndex !== -1 ? rawString.substring(colonIndex + 1) : rawString;
    const trimmed = withoutPrefix.trim();

    if (!trimmed) {
      return null;
    }

    const lower = trimmed.toLowerCase();
    if (lower === 'null' || lower === 'undefined') {
      return null;
    }

    const parsed = toNumber(trimmed);
    return parsed === null ? null : parsed;
  }

  private getCampaignFilterId(): number {
    const raw = this.campaignFilter;
    if (raw === null || raw === undefined) {
      return null;
    }

    if (typeof raw === 'number') {
      const parsedNumber = toNumber(raw);
      return parsedNumber === null ? null : parsedNumber;
    }

    const rawString = String(raw);
    const colonIndex = rawString.indexOf(':');
    const withoutPrefix = colonIndex !== -1 ? rawString.substring(colonIndex + 1) : rawString;
    const trimmed = withoutPrefix.trim();

    if (!trimmed) {
      return null;
    }

    const lower = trimmed.toLowerCase();
    if (lower === 'null' || lower === 'undefined') {
      return null;
    }

    const parsed = toNumber(trimmed);
    return parsed === null ? null : parsed;
  }

  private resolveSpecyIdFromKey(key: string): number {
    if (!this.species) return null;
    const norm = (key || '').toLowerCase();

    const hit = this.species.find((s) => ((s.label || '') as any).toLowerCase() === norm);
    if (hit) return (hit as any).id;

    // fuzzy match ble/colza/orge
    const unaccent = (s: string) => (s || '').toLowerCase().replace('é', 'e');
    for (const s of this.species) {
      const lbl = unaccent(s.label as any);
      if (
        (norm === 'ble' && lbl.indexOf('ble') >= 0) ||
        (norm === 'colza' && lbl.indexOf('colza') >= 0) ||
        (norm === 'orge' && lbl.indexOf('orge') >= 0)
      ) {
        return (s as any).id;
      }
    }
    return null;
  }
}
